import React from "react";
import { HiOfficeBuilding } from "react-icons/hi";

import PageHero from "../../components/common/PageHero";
import PageSectionHeader from "../../components/common/PageSectionHeader";
import ScrollIndicator from "../Home/ScrollIndicator";
import useScrollToTop from "../../hooks/useScollToTop";

import ServiceCard from "./components/ServiceCard";
import ServiceEmptyState from "./components/ServiceEmptyState";
import ServiceFilters from "./components/ServiceFilters";
import ServiceSkeleton from "./components/ServiceSkeleton";

import { SERVICES_MUNICIPAUX_PAGE } from "./data/servicesMunicipauxUiData";
import { useServicesMunicipauxPage } from "./hooks/useServicesMunicipauxPage";

export default function ServicesMunicipauxPage() {
  useScrollToTop();

  const {
    loading,
    error,
    items,
    total,
    allTotal,
    categories,
    filters,
    expandedMapId,
    expandedDetailsId,
    updateFilter,
    toggleCategory,
    resetFilters,
    toggleMap,
    toggleDetails,
    refresh,
  } = useServicesMunicipauxPage();

  return (
    <>
      <PageHero
        title={SERVICES_MUNICIPAUX_PAGE.title}
        imageUrl={SERVICES_MUNICIPAUX_PAGE.heroImageUrl}
        crumbs={SERVICES_MUNICIPAUX_PAGE.crumbs}
      />

      <ScrollIndicator />

      <main className="mx-auto max-w-[1240px] px-4 pb-16 pt-6 sm:px-6 md:pt-8">
        <PageSectionHeader
          eyebrow={SERVICES_MUNICIPAUX_PAGE.eyebrow}
          title={SERVICES_MUNICIPAUX_PAGE.title}
          description={SERVICES_MUNICIPAUX_PAGE.description}
        >
          <div className="rounded-3xl border border-slate-200 bg-white/80 px-5 py-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Services publics
            </p>
            <p className="mt-1 text-3xl font-bold text-slate-950 dark:text-white">
              {loading ? "…" : allTotal}
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              service{allTotal > 1 ? "s" : ""} disponible
              {allTotal > 1 ? "s" : ""}
            </p>
          </div>
        </PageSectionHeader>

        <div className="mt-5 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
              <HiOfficeBuilding className="h-6 w-6" />
            </span>

            <div>
              <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                Trouver rapidement le bon interlocuteur
              </h2>
              <p className="mt-1 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                Utilisez la recherche pour retrouver un service par nom,
                mission, téléphone, email, adresse ou horaires. Les coordonnées
                essentielles restent visibles directement sur chaque fiche.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <ServiceFilters
            loading={loading}
            filters={filters}
            categories={categories}
            total={total}
            allTotal={allTotal}
            onChange={updateFilter}
            onCategoryClick={toggleCategory}
            onReset={resetFilters}
            onRefresh={refresh}
          />
        </div>

        {error && (
          <div className="mt-5 rounded-3xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
            Une erreur est survenue pendant le chargement des services
            municipaux.
          </div>
        )}

        <section className="mt-6">
          {loading ? (
            <ServiceSkeleton />
          ) : items.length > 0 ? (
            <div className="space-y-5">
              {items.map((service, index) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  delay={Math.min(index * 35, 180)}
                  isMapOpen={expandedMapId === service.id}
                  isDetailsOpen={expandedDetailsId === service.id}
                  onToggleMap={() => toggleMap(service.id)}
                  onToggleDetails={() => toggleDetails(service.id)}
                />
              ))}
            </div>
          ) : (
            <ServiceEmptyState onReset={resetFilters} />
          )}
        </section>
      </main>
    </>
  );
}